import React, { useContext, useEffect } from "react";
import usePendoTrackEvent from "../../../shared/use_pendo_track_event";
import styled from "styled-components";
import { SurveyType } from "../../../generated/graphql";
import NotificationForQuestionErrors from "./notification_for_question_errors";
import { SurveyState } from "../../survey";
import { AppContext, Environment } from "../../../../components/app";
import { SurveyAudienceContext } from "../../survey_page";

const StyledNotificationWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

const StyledNotificationContainer = styled.div`
  width: 100%;
`;

export default function useSurveyValidation(surveyState: SurveyState) {
  const { environment } = useContext(AppContext);

  const { survey, ableToSubmit, questionsWithErrors, showValidation, setShowValidation } =
    surveyState;

  const surveyType = survey.surveyType ? `-${survey.surveyType}` : "";

  const QUICK_CONVERSATION_LOGGING_SURVEY_ID = "28";

  // TA-879: due to time and resource constraints, these surveys will not have a refreshed UI
  const showRefreshedComponents =
    survey.surveyType !== SurveyType.PostSession &&
    survey.id !== QUICK_CONVERSATION_LOGGING_SURVEY_ID;

  const pendoTrackEvent = usePendoTrackEvent();

  useEffect(() => {
    if (showValidation) {
      const element = document.getElementById("survey-validation-message");
      element?.scrollIntoView({
        // smooth behavior causes flake in e2e tests because they're so fast, so we show the error notification "instantly" there
        behavior: environment === Environment.Test ? "auto" : "smooth",
        block: "end",
        inline: "nearest",
      });
    }
  }, [showValidation, environment]);

  const renderErrorNotification = (context?: SurveyAudienceContext) => {
    return (
      showValidation &&
      questionsWithErrors.length > 0 && (
        <StyledNotificationContainer>
          <StyledNotificationWrapper>
            <NotificationForQuestionErrors
              questionsWithErrors={questionsWithErrors}
              onClose={() => setShowValidation(false)}
              surveyAudienceContext={context}
            />
          </StyledNotificationWrapper>
        </StyledNotificationContainer>
      )
    );
  };

  const validateSurvey = async () => {
    // clear out any previous errors
    await setShowValidation(false);
    if (!ableToSubmit) {
      pendoTrackEvent(`user-attempted-to-submit-survey-with-errors${surveyType}`);
      setShowValidation(true);
      throw new Error("Survey has errors that must be addressed before submitting.");
    } else {
      pendoTrackEvent(`user-submitted-survey${surveyType}`);
    }
  };

  return showRefreshedComponents
    ? {
        renderErrorNotification,
        validateSurvey,
        showValidation,
      }
    : {
        renderErrorNotification: () => null,
        validateSurvey: async () => true,
        showValidation: false,
      };
}
